// Auto-extracted tab: regulatory
import React, { useState, useEffect, useMemo, useCallback } from 'react';

/**
 * RegulatoryTab - extracted from app.jsx
 * Original lines: 9536-10755
 * 
 * TODO: This component needs props passed from AppContent:
 * - darkMode, scenarios, thresholds, etc.
 * - All the state and handlers it uses
 */
export function RegulatoryTab(props) {
  // Original render content (needs refactoring to use props):
  return (
    <>
          <div className="space-y-6">
            {/* UPSELL BANNER - Shown when feeds are not enabled (core tier) */}
            {!feedsEnabled && (
              <div className={`bg-gradient-to-r ${darkMode ? "from-amber-900/30 to-orange-900/30 border-amber-700" : "from-amber-50 to-orange-50 border-amber-200"} border-2 border-dashed rounded-xl p-6`}>
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 ${darkMode ? "bg-amber-900/50" : "bg-amber-100"} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <span className="text-2xl">📡</span>
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-bold text-lg mb-2 ${darkMode ? "text-amber-200" : "text-amber-900"}`}>
                      Unlock Live Regulatory Feeds
                    </h3>
                    <p className={`mb-4 ${darkMode ? "text-amber-300/80" : "text-amber-800"}`}>
                      Your current plan includes manual compliance documentation. Upgrade to receive <strong>real-time regulatory updates</strong> from {VERTICAL_CONFIG[vertical]?.displayName || vertical} regulators, automatically mapped to your controls.
                    </p>
                    <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 text-sm ${darkMode ? "text-amber-300/70" : "text-amber-700"}`}>
                      <div className="flex items-center gap-2">
                        <span>✓</span>
                        <span>Daily RSS polling from 50+ regulators</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span>✓</span>
                        <span>AI-assessed impact levels</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span>✓</span>
                        <span>Blocks attestation until reviewed</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span>✓</span>
                        <span>Federal Register API integration</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span>✓</span>
                        <span>Historical archive scraping</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span>✓</span>
                        <span>Auto-mapped to your verticals</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3 items-center">
                      <Button
                        variant="primary"
                        onClick={() => {
                          // Opens pricing calculator page with pre-filled vertical
                          const upgradeUrl = `https://steadfastly.com/upgrade?vertical=${encodeURIComponent(vertical)}&jurisdiction=${encodeURIComponent(jurisdiction)}&org=${encodeURIComponent(org?.id || "")}`;
                          window.open(upgradeUrl, "_blank");
                        }}
                      >
                        <Icons.Star /> View Pricing & Upgrade
                      </Button>
                      <span className={`text-sm ${darkMode ? "text-amber-400/60" : "text-amber-600"}`}>
                        Pricing calculator based on your vertical & company size
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Live Feed Status Banner - Only shown when feeds are enabled */}
            {feedsEnabled && (
            <div className={`bg-gradient-to-r ${darkMode ? "from-slate-800 to-slate-700 border-slate-600" : "from-blue-50 to-emerald-50 border-blue-200"} border rounded-xl p-4`}>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${darkMode ? "bg-blue-900/50" : "bg-blue-100"} rounded-lg flex items-center justify-center`}>
                    <Icons.Bell className={`w-5 h-5 ${darkMode ? "text-blue-400" : "text-blue-600"}`} />
                  </div>
                  <div>
                    <div className={`font-semibold ${darkMode ? "text-white" : "text-slate-900"}`}>
                      Live {VERTICAL_CONFIG[vertical]?.displayName || vertical} Regulatory Feed
                    </div>
                    <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-600"}`}>
                      {vertical === "banking" && jurisdiction === "US" && "Fetching from OCC, FDIC, Federal Reserve, FinCEN, CFPB, FFIEC"}
                      {vertical === "banking" && jurisdiction !== "US" && "Fetching from FCA, EBA, BaFin, FINMA, HKMA, ECB SSM + local regulators"}
                      {vertical === "ria" && "Fetching from SEC, FINRA, FinCEN, Treasury, OFAC"}
                      {vertical === "nuclear" && "Fetching from NRC, DOE"}
                      {vertical === "defense" && "Fetching from DoD, BIS, NIST, CISA, Treasury, OFAC"}
                      {vertical === "aviation" && "Fetching from FAA, TSA, NTSB + EASA (EU)"}
                      {vertical === "automotive" && "Fetching from NHTSA, EPA, OSHA"}
                      {vertical === "energy" && "Fetching from FERC, NERC, DOE, PHMSA, EPA"}
                      {vertical === "healthcare" && "Fetching from HHS, CMS, FDA, CDC, OCR"}
                      {vertical === "manufacturing" && "Fetching from OSHA, CPSC, EPA, NIST, MSHA"}
                      {vertical === "ai" && "Fetching from FTC, NIST, SEC, White House OSTP, EU AI Act sources, State AGs"}
                      {vertical === "grc" && "Fetching from OSHA, EPA, FTC, IRS, NIST, AICPA"}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {/* Poll All Feeds Button - PRIMARY ACTION */}
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={pollAllFeeds}
                    disabled={regLoading || regScraping}
                    title="Fetch new updates from all active RSS feeds"
                  >
                    {regScraping ? (
                      <>
                        <Icons.Clock className="animate-spin" /> Fetching Updates...
                      </>
                    ) : (
                      <>
                        <Icons.Download /> Fetch New Updates
                      </>
                    )}
                  </Button>
                  
                  {/* Reload from database */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={loadRegulatoryData}
                    disabled={regLoading}
                    title="Reload updates from database"
                  >
                    {regLoading ? (
                      <Icons.Clock className="animate-spin" />
                    ) : (
                      <Icons.Refresh />
                    )}
                  </Button>
                </div>
              </div>
            </div>
            )}

            {/* Manual Tracking Mode - Shown when feeds are NOT enabled (core tier) */}
            {!feedsEnabled && (
              <Card
                title="📋 Manual Regulatory Tracking"
                subtitle="Track regulatory changes manually until you upgrade to automated feeds"
                darkMode={darkMode}
              >
                <div className="space-y-4">
                  <p className={`text-sm ${darkMode ? "text-slate-400" : "text-slate-600"}`}>
                    You can still track regulatory changes manually. Visit these regulator websites, download relevant guidance, and upload documents to your Evidence Library.
                  </p>
                  
                  {/* Quick Links to Regulators */}
                  <div>
                    <h4 className={`font-medium mb-2 ${darkMode ? "text-white" : "text-slate-900"}`}>
                      Key {VERTICAL_CONFIG[vertical]?.displayName || vertical} Regulators
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {vertical === "banking" && jurisdiction === "US" && (
                        <>
                          <a href="https://www.occ.gov/news-events" target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 p-2 rounded-lg ${darkMode ? "bg-slate-700 hover:bg-slate-600" : "bg-slate-100 hover:bg-slate-200"}`}>
                            <span>🏛️</span> <span className="text-sm">OCC</span> <Icons.ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                          </a>
                          <a href="https://www.fdic.gov/news" target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 p-2 rounded-lg ${darkMode ? "bg-slate-700 hover:bg-slate-600" : "bg-slate-100 hover:bg-slate-200"}`}>
                            <span>🏦</span> <span className="text-sm">FDIC</span> <Icons.ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                          </a>
                          <a href="https://www.federalreserve.gov/newsevents.htm" target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 p-2 rounded-lg ${darkMode ? "bg-slate-700 hover:bg-slate-600" : "bg-slate-100 hover:bg-slate-200"}`}>
                            <span>🏛️</span> <span className="text-sm">Federal Reserve</span> <Icons.ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                          </a>
                          <a href="https://www.fincen.gov/news" target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 p-2 rounded-lg ${darkMode ? "bg-slate-700 hover:bg-slate-600" : "bg-slate-100 hover:bg-slate-200"}`}>
                            <span>🔍</span> <span className="text-sm">FinCEN</span> <Icons.ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                          </a>
                        </>
                      )}
                      {vertical === "healthcare" && (
                        <>
                          <a href="https://www.hhs.gov/about/news" target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 p-2 rounded-lg ${darkMode ? "bg-slate-700 hover:bg-slate-600" : "bg-slate-100 hover:bg-slate-200"}`}>
                            <span>🏥</span> <span className="text-sm">HHS</span> <Icons.ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                          </a>
                          <a href="https://www.cms.gov/newsroom" target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 p-2 rounded-lg ${darkMode ? "bg-slate-700 hover:bg-slate-600" : "bg-slate-100 hover:bg-slate-200"}`}>
                            <span>🏛️</span> <span className="text-sm">CMS</span> <Icons.ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                          </a>
                          <a href="https://www.fda.gov/news-events" target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 p-2 rounded-lg ${darkMode ? "bg-slate-700 hover:bg-slate-600" : "bg-slate-100 hover:bg-slate-200"}`}>
                            <span>💊</span> <span className="text-sm">FDA</span> <Icons.ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                          </a>
                        </>
                      )}
                      {vertical === "energy" && (
                        <>
                          <a href="https://www.ferc.gov/news-events" target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 p-2 rounded-lg ${darkMode ? "bg-slate-700 hover:bg-slate-600" : "bg-slate-100 hover:bg-slate-200"}`}>
                            <span>⚡</span> <span className="text-sm">FERC</span> <Icons.ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                          </a>
                          <a href="https://www.nerc.com/news" target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 p-2 rounded-lg ${darkMode ? "bg-slate-700 hover:bg-slate-600" : "bg-slate-100 hover:bg-slate-200"}`}>
                            <span>🔌</span> <span className="text-sm">NERC</span> <Icons.ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                          </a>
                        </>
                      )}
                      {/* Default - Federal Register for all verticals */}
                      <a href="https://www.federalregister.gov" target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 p-2 rounded-lg ${darkMode ? "bg-slate-700 hover:bg-slate-600" : "bg-slate-100 hover:bg-slate-200"}`}>
                        <span>📜</span> <span className="text-sm">Federal Register</span> <Icons.ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                      </a>
                    </div>
                  </div>
                  
                  <div className={`p-4 rounded-lg ${darkMode ? "bg-slate-700/50" : "bg-blue-50"}`}>
                    <p className={`text-sm ${darkMode ? "text-blue-300" : "text-blue-800"}`}>
                      <strong>💡 Tip:</strong> When you find a regulatory update, go to the <strong>Evidence Library</strong> tab to upload it. 
                      Then link it to relevant Scenarios or Thresholds to document your review.
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* Custom Feeds (user-added) */}
            {/* Active Regulatory Feeds from Database - Only shown when feeds are enabled */}
            {feedsEnabled && jurisdictionFeeds.length > 0 && (
              <Card 
                title={`🌍 Active Regulatory Feeds (${jurisdiction})`}
                subtitle={`${jurisdictionFeeds.filter(f => f.is_active && f.feed_type === 'rss').length} RSS feeds polling daily`}
                darkMode={darkMode}
                actions={
                  <div className="flex gap-2">
                    <Button variant="primary" size="sm" onClick={pollAllFeeds} disabled={regScraping}>
                      {regScraping ? <><Icons.Clock className="animate-spin" /> Polling...</> : <><Icons.Download /> Poll Now</>}
                    </Button>
                  </div>
                }
              >
                <div className="space-y-4">
                  {/* Active RSS Feeds */}
                  <div>
                    <div className={`text-sm font-medium ${darkMode ? "text-emerald-400" : "text-emerald-700"} mb-2 flex items-center gap-2`}>
                      <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                      Active RSS/Atom Feeds (Auto-polled)
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {jurisdictionFeeds.filter(f => f.is_active && (f.feed_type === 'rss' || f.feed_type === 'atom')).map(feed => (
                        <div key={feed.id} className={`flex items-center gap-2 p-2 rounded-lg ${darkMode ? "bg-slate-700/50" : "bg-emerald-50"}`}>
                          <div className="min-w-0 flex-1">
                            <div className={`text-sm font-medium truncate ${darkMode ? "text-white" : "text-slate-900"}`}>
                              {feed.regulator_acronym || feed.name}
                            </div>
                            <div className={`text-xs truncate ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                              {feed.name}
                            </div>
                          </div>
                          <Badge color="emerald" size="sm">{feed.feed_type?.toUpperCase()}</Badge>
                        </div>
                      ))}
                    </div>
                    {jurisdictionFeeds.filter(f => f.is_active && (f.feed_type === 'rss' || f.feed_type === 'atom')).length === 0 && (
                      <p className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                        No active RSS feeds. Expand "📡 RSS Feed Configuration" below to add feeds for your regulators.
                      </p>
                    )}
                  </div>
                  
                  {/* Scrape/Manual Sources */}
                  {jurisdictionFeeds.filter(f => f.is_active && f.feed_type === 'scrape').length > 0 && (
                    <div>
                      <div className={`text-sm font-medium ${darkMode ? "text-amber-400" : "text-amber-700"} mb-2`}>
                        📄 Manual Monitoring (No RSS Available)
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {jurisdictionFeeds.filter(f => f.is_active && f.feed_type === 'scrape').map(feed => (
                          <a
                            key={feed.id}
                            href={feed.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`flex items-center gap-2 p-2 rounded-lg ${darkMode ? "bg-slate-700/50 hover:bg-slate-600/50" : "bg-amber-50 hover:bg-amber-100"} transition-colors`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className={`text-sm font-medium truncate ${darkMode ? "text-white" : "text-slate-900"}`}>
                                {feed.regulator_acronym || feed.name}
                              </div>
                              <div className={`text-xs truncate ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                                {feed.name}
                              </div>
                            </div>
                            <Icons.ExternalLink className="w-4 h-4 text-amber-600 flex-shrink-0" />
                          </a>
                        ))}
                      </div>
                      <p className={`text-xs mt-2 ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                        💡 These regulators don't provide RSS. Click to open their page → Download PDFs → Upload to Steadfastly.
                      </p>
                    </div>
                  )}
                </div>
              </Card>
            )}
            
            {/* RSS Feeds Configuration - Collapsible */}
            {!dismissedCards.includes('rss-feeds-config') && (
            <div className={`rounded-xl border ${darkMode ? "border-slate-700 bg-slate-800/50" : "border-slate-200 bg-white"}`}>
              {/* Collapsible Header */}
              <button
                onClick={() => toggleSectionCollapse('rss-feeds-config')}
                className={`w-full px-6 py-4 flex items-center justify-between ${darkMode ? "hover:bg-slate-700/50" : "hover:bg-slate-50"} rounded-t-xl transition-colors`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">📡</span>
                  <div className="text-left">
                    <h3 className={`font-semibold ${darkMode ? "text-white" : "text-slate-900"}`}>
                      RSS Feed Configuration
                    </h3>
                    <p className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                      {regulatoryFeeds.filter(f => f.feed_type === 'rss').length} active • {getRSSFeeds().length - regulatoryFeeds.filter(f => f.feed_type === 'rss').length} available
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded ${isSectionCollapsed('rss-feeds-config') ? (darkMode ? "bg-slate-600" : "bg-slate-200") : (darkMode ? "bg-blue-600" : "bg-blue-100 text-blue-700")}`}>
                    {isSectionCollapsed('rss-feeds-config') ? "Expand to configure" : "Collapse when done"}
                  </span>
                  <Icons.ChevronDown className={`w-5 h-5 transition-transform ${isSectionCollapsed('rss-feeds-config') ? "" : "rotate-180"} ${darkMode ? "text-slate-500" : "text-slate-500"}`} />
                </div>
              </button>
              
              {/* Collapsible Content */}
              {!isSectionCollapsed('rss-feeds-config') && (
              <div className={`px-6 pb-6 border-t ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
                <div className="pt-4 space-y-4">
                  <p className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-600"}`}>
                    <strong>Click to subscribe/unsubscribe.</strong> US Banking: OCC, FDIC, Fed, FinCEN, CFPB. 
                    <span className={`ml-2 px-2 py-0.5 rounded text-xs ${darkMode ? "bg-emerald-800 text-emerald-200" : "bg-emerald-100 text-emerald-700"}`}>
                      {addedFeedCount} subscribed
                    </span>
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {getAvailableFeedsSorted().map(feed => {
                      const isAdded = isFeedAdded(feed.url);
                      const isLoading = importingFeedId === feed.id;
                      
                      return (
                        <button
                          key={feed.id}
                          onClick={() => isAdded ? removeFeed(feed.url) : importSingleFeed(feed)}
                          disabled={isLoading}
                          className={`flex items-center gap-2 p-3 rounded-lg text-left transition-all border ${
                            isLoading 
                              ? (darkMode ? "bg-blue-900/50 border-blue-500" : "bg-blue-100 border-blue-400")
                              : isAdded
                                ? (darkMode 
                                    ? "bg-emerald-900/30 border-emerald-600/50 hover:bg-red-900/30 hover:border-red-500/50" 
                                    : "bg-emerald-50 border-emerald-300 hover:bg-red-50 hover:border-red-300")
                                : (darkMode 
                                    ? "bg-slate-700/50 hover:bg-slate-600/70 border-transparent hover:border-blue-500/50" 
                                    : "bg-slate-50 hover:bg-blue-50 border-transparent hover:border-blue-400/50")
                          } cursor-pointer group`}
                        >
                          <span className="text-lg">{JURISDICTION_CONFIG[feed.jurisdiction_code]?.flag || "🌐"}</span>
                          <div className="min-w-0 flex-1">
                            <div className={`text-sm font-medium truncate ${darkMode ? "text-white" : "text-slate-900"}`}>
                              {feed.regulator_acronym}
                            </div>
                            <div className={`text-xs truncate ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                              {feed.name}
                            </div>
                          </div>
                          {isLoading ? (
                            <span className={`text-xs px-2 py-1 rounded ${darkMode ? "bg-blue-600 text-white" : "bg-blue-500 text-white"}`}>
                              Adding...
                            </span>
                          ) : isAdded ? (
                            <span className={`text-xs px-2 py-1 rounded ${darkMode ? "bg-emerald-600 text-white" : "bg-emerald-500 text-white"}`}>
                              ✓ Subscribed
                            </span>
                          ) : (
                            <span className={`text-xs px-2 py-1 rounded ${darkMode ? "bg-slate-600 text-white" : "bg-slate-500 text-white"}`}>
                              Subscribe
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Manual PDF Sources */}
                  <div className={`border-t pt-4 mt-4 ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
                    <div className={`text-sm font-medium ${darkMode ? "text-amber-400" : "text-amber-700"} mb-2`}>
                      📄 Regulators Without RSS (Manual PDF Upload)
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {getManualFeeds().map(feed => (
                        <a
                          key={feed.id}
                          href={feed.publications_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center gap-2 p-2 rounded-lg ${darkMode ? "bg-slate-700/50 hover:bg-slate-600/50" : "bg-amber-50 hover:bg-amber-100"} transition-colors`}
                        >
                          <span className="text-lg">{JURISDICTION_CONFIG[feed.jurisdiction_code]?.flag || "🌐"}</span>
                          <div className="min-w-0 flex-1">
                            <div className={`text-sm font-medium truncate ${darkMode ? "text-white" : "text-slate-900"}`}>
                              {feed.regulator_acronym}
                            </div>
                            <div className={`text-xs truncate ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                              {feed.description}
                            </div>
                          </div>
                          <Icons.ExternalLink className="w-4 h-4 text-amber-600 flex-shrink-0" />
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
            )}
            
            {/* Pre-Extracted Rules Starter Pack - Collapsible */}
            {!dismissedCards.includes('starter-pack') && (
            <div className={`rounded-xl border ${darkMode ? "border-slate-700 bg-slate-800/50" : "border-slate-200 bg-white"}`}>
              {/* Collapsible Header */}
              <button
                onClick={() => toggleSectionCollapse('starter-pack')}
                className={`w-full px-6 py-4 flex items-center justify-between ${darkMode ? "hover:bg-slate-700/50" : "hover:bg-slate-50"} rounded-t-xl transition-colors`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">📦</span>
                  <div className="text-left">
                    <h3 className={`font-semibold ${darkMode ? "text-white" : "text-slate-900"}`}>
                      Regulatory Rules Starter Pack
                    </h3>
                    <p className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                      {REGULATORY_STARTER_PACK.fca_ps25_23.rules.length + REGULATORY_STARTER_PACK.eba_gl_2026_01.rules.length} pre-extracted rules with Nine Questions
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded ${isSectionCollapsed('starter-pack') ? (darkMode ? "bg-slate-600" : "bg-slate-200") : (darkMode ? "bg-amber-600" : "bg-amber-100 text-amber-700")}`}>
                    {isSectionCollapsed('starter-pack') ? "Expand to view" : "Collapse when done"}
                  </span>
                  <Icons.ChevronDown className={`w-5 h-5 transition-transform ${isSectionCollapsed('starter-pack') ? "" : "rotate-180"} ${darkMode ? "text-slate-500" : "text-slate-500"}`} />
                </div>
              </button>
              
              {/* Collapsible Content */}
              {!isSectionCollapsed('starter-pack') && (
              <div className={`px-6 pb-6 border-t ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
                <div className="pt-4 space-y-4">
                  <p className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-600"}`}>
                    These rules were extracted from recent regulatory PDFs using AI + human review.
                    Load them for HITL review or import directly to your Policy Manual.
                  </p>
                  
                  {/* FCA PS25/23 */}
                  <div className={`border rounded-lg p-4 ${darkMode ? "border-slate-700 bg-slate-800/50" : "border-slate-200 bg-white"}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">🇬🇧</span>
                          <span className={`font-semibold ${darkMode ? "text-white" : "text-slate-900"}`}>FCA PS25/23</span>
                          <Badge color="blue">UK</Badge>
                          <Badge color="amber">{REGULATORY_STARTER_PACK.fca_ps25_23.rules.length} rules</Badge>
                        </div>
                        <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-600"}`}>
                          {REGULATORY_STARTER_PACK.fca_ps25_23.metadata.title}
                        </div>
                        <div className={`text-xs mt-1 ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                          Effective: {REGULATORY_STARTER_PACK.fca_ps25_23.metadata.effective_date} | 
                          Published: {REGULATORY_STARTER_PACK.fca_ps25_23.metadata.publication_date}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button 
                          variant="primary" 
                          size="sm" 
                          onClick={() => loadStarterPackRules('fca_ps25_23')}
                        >
                          Load for Review
                        </Button>
                        <a 
                          href={REGULATORY_STARTER_PACK.fca_ps25_23.metadata.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`text-xs text-center ${darkMode ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-800"}`}
                        >
                          View Source PDF ↗
                        </a>
                      </div>
                    </div>
                  </div>
                  
                  {/* EBA GL/2026/01 */}
                  <div className={`border rounded-lg p-4 ${darkMode ? "border-slate-700 bg-slate-800/50" : "border-slate-200 bg-white"}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">🇪🇺</span>
                          <span className={`font-semibold ${darkMode ? "text-white" : "text-slate-900"}`}>EBA GL/2026/01</span>
                          <Badge color="blue">EU</Badge>
                          <Badge color="amber">{REGULATORY_STARTER_PACK.eba_gl_2026_01.rules.length} rules</Badge>
                        </div>
                        <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-600"}`}>
                          {REGULATORY_STARTER_PACK.eba_gl_2026_01.metadata.title}
                        </div>
                        <div className={`text-xs mt-1 ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                          Effective: {REGULATORY_STARTER_PACK.eba_gl_2026_01.metadata.effective_date} | 
                          Published: {REGULATORY_STARTER_PACK.eba_gl_2026_01.metadata.publication_date}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button 
                          variant="primary" 
                          size="sm" 
                          onClick={() => loadStarterPackRules('eba_gl_2026_01')}
                        >
                          Load for Review
                        </Button>
                        <a 
                          href={REGULATORY_STARTER_PACK.eba_gl_2026_01.metadata.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`text-xs text-center ${darkMode ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-800"}`}
                        >
                          View Source PDF ↗
                        </a>
                      </div>
                    </div>
                  </div>
                  
                  <p className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                    💡 Click "Load for Review" to open rules in the HITL editor where you can edit, add, or remove rules before adopting to your Policy Manual.
                  </p>
                </div>
              </div>
              )}
            </div>
            )}
            
            {/* Restore dismissed cards link */}
            {dismissedCards.length > 0 && (
              <div className={`text-center py-2`}>
                <button
                  onClick={restoreDismissedCards}
                  className={`text-xs ${darkMode ? "text-slate-500 hover:text-slate-300" : "text-slate-500 hover:text-slate-600"} transition-colors`}
                >
                  Restore {dismissedCards.length} hidden card{dismissedCards.length !== 1 ? "s" : ""}
                </button>
              </div>
            )}
            
            {/* Custom Feeds (user-added) */}
            {regulatoryFeeds.length > 0 && (
              <Card 
                title="Custom Feeds" 
                subtitle={`${regulatoryFeeds.length} custom feed${regulatoryFeeds.length !== 1 ? "s" : ""}`}
                darkMode={darkMode}
                actions={
                  <Button variant="secondary" size="sm" onClick={() => setAddFeedModal(true)}>
                    + Add Custom Feed
                  </Button>
                }
              >
                <div className="space-y-2">
                  {regulatoryFeeds.map(feed => (
                    <div key={feed.id} className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? "bg-slate-700/50" : "bg-slate-50"}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${feed.enabled ? "bg-emerald-500" : "bg-slate-300"}`} />
                        <div>
                          <div className={`font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>{feed.name}</div>
                          <div className={`text-xs truncate max-w-md ${darkMode ? "text-slate-500" : "text-slate-500"}`}>{feed.url}</div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleFeed(feed.id, feed.enabled)}
                      >
                        {feed.enabled ? "Disable" : "Enable"}
                      </Button>
                    </div>
                  ))}
                </div>
              </Card>
            )}
            
            {/* Compliance Report Card */}
            {/* Compliance Review Status - Only CRITICAL/HIGH items count as "Needs Review" */}
            {(() => {
              // Calculate review stats based on impact level
              // CRITICAL/HIGH = Must review (affects attestation)
              // MEDIUM = Optional review (user discretion)
              // LOW = Informational only (never needs review)
              const criticalHighItems = regulatoryUpdates.filter(u => 
                u.impact_guess === "critical" || u.impact_guess === "high"
              );
              const criticalHighPending = criticalHighItems.filter(u => 
                u.review_status === "pending" || !u.review_status
              );
              const criticalHighReviewed = criticalHighItems.filter(u => 
                u.review_status === "reviewed"
              );
              const mediumItems = regulatoryUpdates.filter(u => u.impact_guess === "medium");
              const lowItems = regulatoryUpdates.filter(u => u.impact_guess === "low" || !u.impact_guess);
              
              return (
                <Card darkMode={darkMode} title="Compliance Review Status" subtitle="Only CRITICAL/HIGH items require review">
                  {/* Blocking Banner if CRITICAL/HIGH items are pending */}
                  {criticalHighPending.length > 0 && (
                    <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center gap-3">
                        <Icons.Alert className="w-6 h-6 text-red-600 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-red-900">
                            {criticalHighPending.length} CRITICAL/HIGH item{criticalHighPending.length !== 1 ? "s" : ""} require review
                          </div>
                          <div className="text-sm text-red-700">
                            These items must be reviewed before monthly attestation can proceed.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {criticalHighPending.length === 0 && criticalHighItems.length > 0 && (
                    <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center gap-3">
                        <Icons.Check className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-emerald-900">
                            All CRITICAL/HIGH items reviewed ✓
                          </div>
                          <div className="text-sm text-emerald-700">
                            Attestation is not blocked by regulatory updates.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="bg-red-50 rounded-lg p-4 text-center border-2 border-red-200">
                      <div className="text-2xl font-bold text-red-600">{criticalHighPending.length}</div>
                      <div className="text-xs text-red-700 mt-1 font-medium">CRITICAL/HIGH Pending</div>
                      <div className="text-xs text-red-500">Blocks Attestation</div>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-emerald-600">{criticalHighReviewed.length}</div>
                      <div className="text-xs text-slate-500 mt-1">CRITICAL/HIGH Reviewed</div>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-amber-600">{mediumItems.length}</div>
                      <div className="text-xs text-slate-500 mt-1">MEDIUM (Optional)</div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-slate-500">{lowItems.length}</div>
                      <div className="text-xs text-slate-500 mt-1">LOW (Informational)</div>
                    </div>
                  </div>
                  
                  <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
                    <strong>Review Policy:</strong> CRITICAL/HIGH items must be reviewed before attestation. 
                    MEDIUM items are at your discretion. LOW items are informational only and never require review.
                  </div>
                </Card>
              );
            })()}
            
            {/* Updates List */}
            <Card 
              title="Regulatory Updates" 
              subtitle={`${regulatoryUpdates.length} update${regulatoryUpdates.length !== 1 ? "s" : ""} from live sources`}
              actions={
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={loadRegulatoryData} disabled={regLoading}>
                    {regLoading ? <Icons.Clock className="animate-spin" /> : <Icons.Refresh />} Refresh
                  </Button>
                </div>
              }
            >
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <select
                  className={`border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200 text-slate-900"}`}
                  value={regFilters.status}
                  onChange={e => setRegFilters({ ...regFilters, status: e.target.value })}
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending Review</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="snoozed">Snoozed</option>
                </select>
                <select
                  className={`border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200 text-slate-900"}`}
                  value={regFilters.source}
                  onChange={e => setRegFilters({ ...regFilters, source: e.target.value })}
                >
                  <option value="all">All Sources</option>
                  <option value="OCC">OCC</option>
                  <option value="FDIC">FDIC</option>
                  <option value="Federal Reserve">Federal Reserve</option>
                  <option value="FinCEN">FinCEN</option>
                  <option value="Treasury">Treasury</option>
                  <option value="CFPB">CFPB</option>
                  <option value="FFIEC">FFIEC</option>
                </select>
                <select
                  className={`border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200 text-slate-900"}`}
                  value={regFilters.impact}
                  onChange={e => setRegFilters({ ...regFilters, impact: e.target.value })}
                >
                  <option value="all">All Impact Levels</option>
                  <option value="critical">🔴 Critical (Blocks Attestation)</option>
                  <option value="high">🟠 High (Blocks Attestation)</option>
                  <option value="medium">🟡 Medium (Optional Review)</option>
                  <option value="low">⚪ Low (Informational)</option>
                </select>
                <label className={`flex items-center gap-2 text-sm cursor-pointer px-3 py-1.5 rounded-lg ${darkMode ? "text-slate-300 bg-slate-700" : "text-slate-600 bg-slate-100"}`}>
                  <input
                    type="checkbox"
                    aria-label="Show low impact regulatory updates"
                    checked={regFilters.showLow}
                    onChange={e => setRegFilters({ ...regFilters, showLow: e.target.checked })}
                    className="rounded text-blue-500"
                  />
                  Show LOW items ({regulatoryUpdates.filter(u => u.impact_guess === "low" || !u.impact_guess).length})
                </label>
                <label className={`flex items-center gap-2 text-sm cursor-pointer ${darkMode ? "text-slate-300" : "text-slate-600"}`}>
                  <input
                    type="checkbox"
                    aria-label="Show noise regulatory updates"
                    checked={regFilters.showNoise}
                    onChange={e => setRegFilters({ ...regFilters, showNoise: e.target.checked })}
                    className="rounded text-slate-500"
                  />
                  Show noise
                </label>
              </div>
              
              {/* Info banner about hidden LOW items */}
              {!regFilters.showLow && regulatoryUpdates.filter(u => u.impact_guess === "low" || !u.impact_guess).length > 0 && (
                <div className="mb-4 p-3 bg-slate-100 rounded-lg text-sm text-slate-600 flex items-center justify-between">
                  <span>
                    <strong>{regulatoryUpdates.filter(u => u.impact_guess === "low" || !u.impact_guess).length}</strong> LOW (informational) items hidden. 
                    These don't require review and don't affect your compliance score.
                  </span>
                  <button 
                    className="text-blue-600 hover:underline font-medium"
                    onClick={() => setRegFilters({ ...regFilters, showLow: true })}
                  >
                    Show them
                  </button>
                </div>
              )}
              
              {/* Updates List */}
              {regLoading && regulatoryUpdates.length === 0 ? (
                <div className="text-center py-12">
                  <Icons.Clock className="w-12 h-12 mx-auto mb-4 text-blue-400 animate-spin" />
                  <p className="text-slate-600 font-medium">Loading regulatory updates...</p>
                  <p className="text-sm text-slate-500 mt-1">Connecting to regulatory agencies</p>
                </div>
              ) : regulatoryUpdates.length === 0 ? (
                <div className="text-center py-16">
                  <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4 ${darkMode ? "bg-amber-900/30" : "bg-gradient-to-br from-amber-100 to-orange-100"}`}>
                    <Icons.Bell className={`w-8 h-8 ${darkMode ? "text-amber-400" : "text-amber-500"}`} />
                  </div>
                  <h3 className={`text-lg font-semibold mb-2 ${darkMode ? "text-white" : "text-slate-900"}`}>No regulatory updates yet</h3>
                  <p className={`text-sm max-w-sm mx-auto ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                    First, add RSS feeds below (📡 RSS Feed Configuration), then click "Fetch New Updates"
                  </p>
                  <Button variant="primary" className="mt-4" onClick={pollAllFeeds} disabled={regScraping}>
                    {regScraping ? <><Icons.Clock className="animate-spin" /> Fetching...</> : <><Icons.Download /> Fetch New Updates</>}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {regulatoryUpdates
                    .filter(u => {
                      // Filter out LOW items unless showLow is enabled (KEY USABILITY FIX)
                      const isLow = u.impact_guess === "low" || !u.impact_guess;
                      if (!regFilters.showLow && isLow && regFilters.impact === "all") return false;
                      // Filter out noise unless showNoise is enabled
                      if (!regFilters.showNoise && u.is_relevant === false) return false;
                      // Filter by status
                      if (regFilters.status !== "all" && u.review_status !== regFilters.status) return false;
                      // Filter by source
                      if (regFilters.source !== "all") {
                        const link = (u.link || "").toLowerCase();
                        const source = u.source || "";
                        const sourceMatch =
                          (regFilters.source === "OCC" && (link.includes("occ.gov") || source === "OCC")) ||
                          (regFilters.source === "FDIC" && (link.includes("fdic.gov") || source === "FDIC")) ||
                          (regFilters.source === "Federal Reserve" && (link.includes("federalreserve.gov") || source === "Federal Reserve" || source === "Fed")) ||
                          (regFilters.source === "FinCEN" && (link.includes("fincen.gov") || source === "FinCEN")) ||
                          (regFilters.source === "Treasury" && (link.includes("treasury.gov") || source === "Treasury")) ||
                          (regFilters.source === "CFPB" && (link.includes("consumerfinance.gov") || source === "CFPB")) ||
                          (regFilters.source === "FFIEC" && (link.includes("ffiec.gov") || source === "FFIEC"));
                        if (!sourceMatch) return false;
                      }
                      if (regFilters.impact !== "all" && u.impact_guess !== regFilters.impact) return false;
                      return true;
                    })
                    .map(update => {
                      // Determine source - use high-contrast readable colors
                      const link = (update.link || "").toLowerCase();
                      let source = update.source || "Unknown";
                      let sourceColor = "slate";
                      if (source === "OCC" || link.includes("occ.gov")) { source = "OCC"; sourceColor = "emerald"; }
                      else if (source === "FDIC" || link.includes("fdic.gov")) { source = "FDIC"; sourceColor = "amber"; }
                      else if (source === "Federal Reserve" || source === "Fed" || link.includes("federalreserve.gov")) { source = "Fed"; sourceColor = "blue"; }
                      else if (source === "FinCEN" || link.includes("fincen.gov")) { source = "FinCEN"; sourceColor = "rose"; }
                      else if (source === "Treasury" || link.includes("treasury.gov")) { source = "Treasury"; sourceColor = "teal"; }
                      else if (source === "CFPB" || link.includes("consumerfinance.gov")) { source = "CFPB"; sourceColor = "orange"; }
                      else if (source === "FFIEC" || link.includes("ffiec.gov")) { source = "FFIEC"; sourceColor = "cyan"; }
                      
                      const reviewStatus = update.review_status || "pending";
                      const impactLevel = update.impact_guess || "low";
                      const isHighPriority = impactLevel === "critical" || impactLevel === "high";
                      const isMedium = impactLevel === "medium";
                      const isLow = impactLevel === "low" || !impactLevel;
                      
                      // Determine visual styling based on impact + review status
                      // CRITICAL/HIGH pending = red (blocking)
                      // CRITICAL/HIGH reviewed = green
                      // MEDIUM = amber (optional)
                      // LOW = slate (informational)
                      // Calculate SLA for pending items
                      const sla = (reviewStatus === "pending" || !reviewStatus) ? calculateReviewSLA(update) : null;
                      
                      let borderColor, bgColor, statusColor, statusText;
                      
                      if (isHighPriority) {
                        if (reviewStatus === "reviewed") {
                          borderColor = "border-emerald-400";
                          bgColor = "bg-emerald-50";
                          statusColor = "emerald";
                          statusText = "Reviewed ✓";
                        } else if (reviewStatus === "snoozed") {
                          borderColor = "border-blue-400";
                          bgColor = "bg-blue-50";
                          statusColor = "blue";
                          statusText = "Snoozed";
                        } else {
                          // Use SLA status for color
                          if (sla?.status === "overdue") {
                            borderColor = "border-red-600";
                            bgColor = "bg-red-100";
                            statusColor = "red";
                            statusText = sla.label;
                          } else if (sla?.status === "warning") {
                            borderColor = "border-amber-500";
                            bgColor = "bg-amber-50";
                            statusColor = "amber";
                            statusText = sla.label;
                          } else {
                            borderColor = "border-red-400";
                            bgColor = "bg-red-50";
                            statusColor = "red";
                            statusText = "⚠️ Review Required";
                          }
                        }
                      } else if (isMedium) {
                        if (reviewStatus === "reviewed") {
                          borderColor = "border-emerald-400";
                          bgColor = "bg-emerald-50";
                          statusColor = "emerald";
                          statusText = "Reviewed";
                        } else {
                          // Show SLA for medium items too
                          if (sla?.status === "overdue") {
                            borderColor = "border-amber-500";
                            bgColor = "bg-amber-100";
                            statusColor = "amber";
                            statusText = sla.label;
                          } else {
                            borderColor = "border-amber-300";
                            bgColor = "bg-amber-50";
                            statusColor = "amber";
                            statusText = "Optional Review";
                          }
                        }
                      } else {
                        // LOW items - informational only, no SLA
                        borderColor = "border-slate-200";
                        bgColor = "bg-slate-50";
                        statusColor = "slate";
                        statusText = "Informational";
                      }
                      
                      return (
                        <div 
                          key={update.id || update.dedupe_key} 
                          className={`p-4 rounded-lg border-l-4 transition cursor-pointer hover:shadow-md ${bgColor} ${borderColor}`}
                          onClick={() => openRegReviewModal(update)}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <Badge color={statusColor} className="text-xs">
                                  {statusText}
                                </Badge>
                                {/* Show SLA timer for pending CRITICAL/HIGH/MEDIUM items */}
                                {sla && sla.status !== "none" && reviewStatus !== "reviewed" && (
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    sla.status === "overdue" ? "bg-red-200 text-red-800" :
                                    sla.status === "warning" ? "bg-amber-200 text-amber-800" :
                                    "bg-emerald-200 text-emerald-800"
                                  }`}>
                                    {sla.label}
                                  </span>
                                )}
                                {update.is_relevant === false && (
                                  <Badge color="slate" className="text-xs">Noise</Badge>
                                )}
                                {update.ai_summary && (
                                  <Badge color="purple" className="text-xs">AI Ready</Badge>
                                )}
                              </div>
                              <div className="font-medium text-slate-900">
                                {update.title}
                              </div>
                              <div className="flex flex-wrap items-center gap-2 mt-1">
                                <Badge color={sourceColor}>{source}</Badge>
                                <span className="text-xs text-slate-500">{formatDate(update.published_at)}</span>
                                {update.impact_guess && (
                                  <Badge color={
                                    update.impact_guess === "critical" ? "red" : 
                                    update.impact_guess === "high" ? "orange" : 
                                    update.impact_guess === "medium" ? "amber" : "slate"
                                  }>
                                    {update.impact_guess === "critical" ? "🔴 CRITICAL" :
                                     update.impact_guess === "high" ? "🟠 HIGH" :
                                     update.impact_guess === "medium" ? "🟡 MEDIUM" : "⚪ LOW"}
                                  </Badge>
                                )}
                              </div>
                              {update.ai_summary ? (
                                <p className="text-sm text-slate-600 mt-2">{update.ai_summary}</p>
                              ) : update.summary ? (
                                <p className="text-sm text-slate-600 mt-2 line-clamp-2">{update.summary}</p>
                              ) : null}
                            </div>
                            <div className="flex flex-col items-center gap-2">
                              <a
                                href={update.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded-lg transition"
                                title="Open original source"
                                onClick={e => e.stopPropagation()}
                              >
                                <Icons.ExternalLink className="w-4 h-4" />
                              </a>
                              {/* Task Assignment Dropdown */}
                              <select
                                className={`text-xs border rounded px-2 py-1 ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200 text-slate-900"}`}
                                value={getAssignment("regulatory_update", update.id)?.assignee_id || ""}
                                onClick={e => e.stopPropagation()}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const member = orgMembers.find(m => m.user_id === e.target.value);
                                  if (member) {
                                    assignTask("regulatory_update", update.id, member.user_id, member.profiles?.email || "User");
                                  }
                                }}
                              >
                                <option value="">Assign to...</option>
                                {orgMembers.map(m => (
                                  <option key={m.user_id} value={m.user_id}>
                                    {m.profiles?.full_name || m.profiles?.email || "User"}
                                  </option>
                                ))}
                              </select>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={(e) => { e.stopPropagation(); openRegReviewModal(update); }}
                              >
                                Review
                              </Button>
                            </div>
                          </div>
                          {/* Assignment indicator */}
                          {getAssignment("regulatory_update", update.id) && (
                            <div className="px-4 py-1.5 bg-blue-50 border-t border-blue-100 text-xs text-blue-700 flex items-center gap-1">
                              <Icons.User className="w-3 h-3" />
                              <span>Assigned to {getAssignment("regulatory_update", update.id).assignee_name}</span>
                              <span className="text-blue-400 ml-1">
                                • {formatDateTime(getAssignment("regulatory_update", update.id).assigned_at)}
                              </span>
                            </div>
                          )}
                          {/* Disclaimer footer */}
                          <div className="px-4 py-1.5 bg-slate-50 border-t border-slate-100 text-xs text-slate-500 flex items-center gap-1">
                            <Icons.Info className="w-3 h-3" />
                            <span>Verify with original source before taking action</span>
                            <a 
                              href={update.link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:underline ml-1"
                              onClick={e => e.stopPropagation()}
                            >
                              View source →
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  
                  {/* Pagination info and Load More button */}
                  <div className="mt-6 text-center border-t border-slate-200 pt-4">
                    <p className="text-sm text-slate-500 mb-3">
                      Showing {regulatoryUpdates.length} of {regPagination.total} regulatory updates
                    </p>
                    {regPagination.hasMore && (
                      <Button
                        variant="secondary"
                        onClick={() => loadRegulatoryData(true)}
                        disabled={regLoadingMore}
                        className="mx-auto"
                      >
                        {regLoadingMore ? (
                          <>
                            <Icons.Clock className="w-4 h-4 mr-2 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <Icons.ChevronDown className="w-4 h-4 mr-2" />
                            Load More ({regPagination.total - regulatoryUpdates.length} remaining)
                          </>
                        )}
                      </Button>
                    )}
                    {!regPagination.hasMore && regulatoryUpdates.length > 0 && (
                      <p className="text-xs text-slate-500">All regulatory updates loaded</p>
                    )}
                  </div>
                </div>
              )}
            </Card>
            
            {/* Decision History */}
            {regDecisions.length > 0 && (
              <Card darkMode={darkMode} title="Decision History" subtitle="Audit trail of all regulatory review decisions">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gradient-to-r from-slate-50 to-slate-100/80">
                      <tr>
                        <th className="px-4 py-3.5 text-left font-semibold text-slate-700">Date</th>
                        <th className="px-4 py-3.5 text-left font-semibold text-slate-700">Update</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Decision</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Decided By</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Reason/Objects</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Source</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {regDecisions.slice(0, 20).map(decision => {
                        const update = regulatoryUpdates.find(u => u.id === decision.regulatory_update_id);
                        return (
                          <tr key={decision.id} className="hover:bg-slate-50">
                            <td className="px-3 py-3 text-slate-500">{formatDateTime(decision.decided_at)}</td>
                            <td className="px-3 py-3 font-medium text-slate-900 max-w-xs truncate">
                              {update?.title || "—"}
                            </td>
                            <td className="px-3 py-3">
                              <Badge color={
                                decision.decision === "adopted" ? "emerald" :
                                decision.decision === "declined" ? "red" : "blue"
                              }>
                                {decision.decision}
                              </Badge>
                            </td>
                            <td className="px-3 py-3 text-slate-500">{decision.decided_by_name || decision.decided_by_email}</td>
                            <td className="px-3 py-3 text-slate-500 max-w-xs truncate">
                              {decision.decision === "declined" ? decision.decline_reason :
                               decision.decision === "adopted" ? `${decision.affected_object_ids?.length || 0} object(s)` :
                               decision.snooze_until ? `Until ${formatDate(decision.snooze_until)}` : "—"}
                            </td>
                            <td className="px-3 py-3">
                              {decision.original_source_url ? (
                                <a 
                                  href={decision.original_source_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-500 hover:text-blue-700"
                                >
                                  <Icons.Link className="w-4 h-4" />
                                </a>
                              ) : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
            
            {/* Policy Manual - Adopted Rules Library */}
            <Card 
              title="Policy Manual" 
              subtitle={`${policyRules.length} adopted compliance rule${policyRules.length !== 1 ? "s" : ""}`}
              badge={<Badge color="emerald">{policyRules.length}</Badge>}
              actions={
                <div className="flex items-center gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={loadPolicyManual}
                    disabled={policyManualLoading}
                  >
                    <Icons.Refresh className={policyManualLoading ? "animate-spin" : ""} /> Refresh
                  </Button>
                  <Button 
                    variant="primary" 
                    size="sm" 
                    onClick={exportPolicyManual}
                    disabled={policyRules.length === 0 || policyManualLoading}
                  >
                    {policyManualLoading ? (
                      <><Icons.Clock className="animate-spin" /> Generating...</>
                    ) : (
                      <><Icons.Download /> Export .docx</>
                    )}
                  </Button>
                </div>
              }
            >
              {policyManualLoading ? (
                <div className="text-center py-8">
                  <Icons.Clock className="w-8 h-8 mx-auto mb-3 text-slate-500 animate-spin" />
                  <p className="text-slate-500">Loading policy manual...</p>
                </div>
              ) : policyRules.length === 0 ? (
                <div className="text-center py-16">
                  <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4 ${darkMode ? "bg-indigo-900/30" : "bg-gradient-to-br from-indigo-100 to-blue-100"}`}>
                    <Icons.File className={`w-8 h-8 ${darkMode ? "text-indigo-400" : "text-indigo-500"}`} />
                  </div>
                  <h3 className={`text-lg font-semibold mb-2 ${darkMode ? "text-white" : "text-slate-900"}`}>No rules adopted yet</h3>
                  <p className={`text-sm max-w-sm mx-auto ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                    Use the "Extract Rules" feature when reviewing regulatory updates to build your policy manual
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {policyRules.map((rule, idx) => (
                    <div key={rule.id} className="bg-gradient-to-r from-emerald-50/50 to-white border border-emerald-100 rounded-lg p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge color="emerald">Rule {idx + 1}</Badge>
                            {rule.source_agency && (
                              <Badge color="slate">{rule.source_agency}</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-900 font-medium">{rule.rule_text}</p>
                          {rule.rationale && (
                            <p className="text-xs text-slate-500 mt-2">
                              <span className="font-medium">Rationale:</span> {rule.rationale}
                            </p>
                          )}
                          {rule.notes && (
                            <p className="text-xs text-slate-500 mt-1">
                              <span className="font-medium">Notes:</span> {rule.notes}
                            </p>
                          )}
                          <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-slate-500">
                            {rule.source_title && (
                              <span title={rule.source_title} className="truncate max-w-xs">
                                Source: {rule.source_title}
                              </span>
                            )}
                            {rule.source_published_at && (
                              <span>Published: {formatDate(rule.source_published_at)}</span>
                            )}
                            {rule.adopted_by_email && (
                              <span>Adopted by: {rule.adopted_by_email}</span>
                            )}
                            {rule.adopted_at && (
                              <span>on {formatDate(rule.adopted_at)}</span>
                            )}
                          </div>
                          {(rule.linked_scenario_ids?.length > 0 || rule.linked_threshold_ids?.length > 0) && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {rule.linked_scenario_ids?.map(sid => {
                                const s = scenarios.find(x => x.scenario_id === sid);
                                return s ? (
                                  <Badge key={sid} color="blue">{s.name}</Badge>
                                ) : null;
                              })}
                              {rule.linked_threshold_ids?.map(tid => {
                                const t = thresholds.find(x => x.threshold_id === tid);
                                return t ? (
                                  <Badge key={tid} color="amber">{t.name}</Badge>
                                ) : null;
                              })}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          {rule.source_url && (
                            <a 
                              href={rule.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition"
                              title="View original source"
                            >
                              <Icons.Link className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
            
            {/* Global Regulatory Database Browser */}
            <Card 
              darkMode={darkMode} 
              title="🌐 Global Regulatory Database" 
              subtitle="Browse 850+ regulators across 56+ countries"
              defaultCollapsed={true}
            >
              <GlobalRegulatorBrowser darkMode={darkMode} supabase={supabase} />
            </Card>
          </div>
    </>
  );
}

export default RegulatoryTab;
